import React from 'react';
import FeatureBadge from '../FeatureBadge';

/**
 * UpgradePromptModal - Modal shown when user tries to access a premium feature
 *
 * Displays:
 * - Feature name and required plan
 * - Plan comparison
 * - Upgrade button
 * - Maybe later button
 *
 * Usage:
 *   const [showUpgrade, setShowUpgrade] = useState(false);
 *   const [upgradeInfo, setUpgradeInfo] = useState(null);
 *
 *   // When API returns 403
 *   if (response.status === 403) {
 *     setUpgradeInfo(response.data);
 *     setShowUpgrade(true);
 *   }
 *
 *   <UpgradePromptModal
 *     isOpen={showUpgrade}
 *     onClose={() => setShowUpgrade(false)}
 *     featureName="Automated Reports"
 *     currentPlan="free"
 *     requiredPlan="pro"
 *     upgradeInfo={upgradeInfo}
 *   />
 */
const UpgradePromptModal = ({
  isOpen,
  onClose,
  featureName,
  currentPlan,
  requiredPlan,
  upgradeInfo,
  onUpgrade
}) => {
  if (!isOpen) return null;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade(requiredPlan);
    } else {
      // Default: navigate to billing page
      window.location.href = '/app#billing';
    }
    onClose();
  };

  const formatFeatureName = (name) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `$${price}/user/month`;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Upgrade Required
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  This feature is not available on your current plan
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Feature Info */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">
                  {formatFeatureName(featureName)}
                </h3>
                <FeatureBadge plan={requiredPlan} size="sm" />
              </div>
              <p className="text-sm text-gray-700">
                Upgrade to <span className="font-semibold">{upgradeInfo?.upgrade_info?.plan_name || requiredPlan.toUpperCase()}</span> to access this feature and unlock more powerful capabilities.
              </p>
            </div>

            {/* Plan Comparison */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Plan Comparison</h4>
              <div className="grid grid-cols-2 gap-4">
                {/* Current Plan */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FeatureBadge plan={currentPlan} size="sm" />
                    <span className="text-xs text-gray-600">(Current)</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {currentPlan === 'free' ? 'Free Plan' : `${formatPrice(0)} per user`}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Limited features
                  </div>
                </div>

                {/* Required Plan */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg relative">
                  <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Recommended
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <FeatureBadge plan={requiredPlan} size="sm" showIcon />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {formatPrice(upgradeInfo?.upgrade_info?.price_per_seat || 0)}
                  </div>
                  <div className="mt-2 text-xs text-gray-700">
                    {upgradeInfo?.upgrade_info?.features?.length || 0} features included
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            {upgradeInfo?.upgrade_info?.features && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  What you'll get with {upgradeInfo.upgrade_info.plan_name}:
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {upgradeInfo.upgrade_info.features.slice(0, 8).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{formatFeatureName(feature)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Seat Info */}
            {upgradeInfo?.upgrade_info?.max_seats && (
              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>
                    <span className="font-semibold">Team size:</span> Up to {upgradeInfo.upgrade_info.max_seats} users
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleUpgrade}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
            >
              Upgrade to {upgradeInfo?.upgrade_info?.plan_name || requiredPlan.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradePromptModal;
